import type { MouseEvent } from "react";

export interface JsonlPreviewLine {
  lineNumber: number;
  raw: string;
  parsed: Record<string, unknown> | null;
  parseError: string | null;
}

export interface JsonlNumericSummary {
  field: string;
  sum: number;
  avg: number;
  min: number;
  max: number;
  count: number;
}

export interface JsonlValueCount {
  value: string;
  count: number;
}

export interface JsonlFieldSummary {
  field: string;
  type: "NUMERIC" | "CATEGORICAL";
  count: number;
  nullCount: number;
  sum?: number;
  avg?: number;
  min?: number;
  max?: number;
  distinctCount?: number;
  topValues?: JsonlValueCount[];
}

export interface JsonlPreviewSummary {
  totalLines: number;
  validLines: number;
  invalidLines: number;
  columns: string[];
  numericSummaries: JsonlNumericSummary[];
  fieldSummaries?: JsonlFieldSummary[];
}

export interface JsonlPreviewPage {
  lines: JsonlPreviewLine[];
  totalElements: number;
  page: number;
  size: number;
  summary: JsonlPreviewSummary | null;
}

export type JsonlFetchPage = (params: {
  page: number;
  size: number;
  search?: string;
}) => Promise<JsonlPreviewPage>;

const MAX_CELL_LENGTH = 80;

export const formatCellValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "—";
  }
  if (typeof value === "object") {
    const json = JSON.stringify(value);
    return json.length > MAX_CELL_LENGTH
      ? `${json.slice(0, MAX_CELL_LENGTH)}…`
      : json;
  }
  const text = String(value);
  return text.length > MAX_CELL_LENGTH
    ? `${text.slice(0, MAX_CELL_LENGTH)}…`
    : text;
};

export const getRowRecord = (
  line: JsonlPreviewLine
): Record<string, unknown> => {
  if (line.parsed) {
    return { lineNumber: line.lineNumber, ...line.parsed };
  }
  return {
    lineNumber: line.lineNumber,
    parseError: line.parseError || "Invalid JSON",
    raw: line.raw,
  };
};

export const buildColumnsFromKeys = (
  keys: string[],
  t: (key: string) => string,
  onColumnClick?: (key: string) => void
) => {
  const orderedKeys = [
    "lineNumber",
    ...keys.filter((key) => key !== "lineNumber"),
  ];

  return orderedKeys.map((key) => ({
    title:
      key === "lineNumber"
        ? t("JsonlViewer-LineNumber")
        : key === "parseError"
          ? t("JsonlViewer-ParseError")
          : key,
    dataIndex: key,
    key,
    ellipsis: true,
    render: (value: unknown) => formatCellValue(value),
    onHeaderCell: () =>
      onColumnClick
        ? {
            onClick: (event: MouseEvent) => {
              event.stopPropagation();
              onColumnClick(key);
            },
            style: { cursor: "pointer" },
            className: "jsonl-viewer-column-header",
          }
        : {},
  }));
};

export const findFieldSummary = (
  field: string,
  summary: JsonlPreviewSummary | null
): JsonlFieldSummary | null => {
  if (!summary) {
    return null;
  }

  if (field === "lineNumber") {
    return {
      field: "lineNumber",
      type: "CATEGORICAL",
      count: summary.totalLines,
      nullCount: 0,
      distinctCount: summary.totalLines,
    };
  }

  return (
    summary.fieldSummaries?.find((item) => item.field === field) ||
    summary.numericSummaries
      .filter((item) => item.field === field)
      .map((item) => ({
        field: item.field,
        type: "NUMERIC" as const,
        count: item.count,
        nullCount: 0,
        sum: item.sum,
        avg: item.avg,
        min: item.min,
        max: item.max,
      }))[0] ||
    null
  );
};

export const formatNumber = (value: number): string =>
  value.toLocaleString("es-AR", { maximumFractionDigits: 2 });
